/* The payment options the source workbook offers, as a picker.
 *
 * `Reference list` in ASA_Gateway_Quotation.xlsx holds five, and the desk picks
 * one on every quotation. In this product the same question was a blank text
 * field, which is the substitution FR-39 exists to undo one level down: the same
 * agreement arrived as "50/50", "50 / 50 payment" and "half up front", and
 * nothing could group or compare them.
 *
 * A CLIENT-SIDE narrowing, exactly as incoterms.ts is. The server stores payment
 * terms as bounded short text (quote/domain.go) and accepts anything within that
 * length, so a quote holding wording that predates this list still reads back
 * and still prints. Nothing here rejects stored data; it only shapes what is
 * offered next — which is why `current` is always carried into the options.
 *
 * The VALUES are the workbook's English strings, stored verbatim, because they
 * are the stored contract and what a five-year-old quote has to keep saying.
 * The offer prints a label per choice, in the offer's language (the server's
 * `docs.PaymentTermsName`, backend/platform/docs/paymentterms.go, which the
 * offer and the invoice share — #126), and legacy text as stored. The LABELS
 * here are translated too: the reader picking one is our
 * colleague, and the client reads the offer's wording, not the picker.
 *
 * THE FIVE STORED STRINGS ARE NOW A SERVER-SIDE CONTRACT (P0-2). Converting a
 * quote reads its stored payment terms against Go constants holding these exact
 * strings (`PaymentTermsChoice`, `backend/internal/quote/paymentterms.go`) to
 * choose the case's payment-terms kind: prepayment, 50/50 and full on delivery
 * map to their own kinds, everything else to `other`. Nothing at RUNTIME fails
 * if a value here changes — every quote saved with the new spelling simply
 * converts as «other», silently — so `paymentOptions.test.ts` reads the Go file
 * and fails when the two lists part. Change a value only together with that constant, and never
 * for a label; the labels are the catalogue's.
 */

/** The five values, in the workbook's own order. */
export const paymentOptionValues = [
  'IN ADVANCE PAYMENT',
  '50/50 PAYMENT (UPON PICKUP&DELIVERY)',
  'FULL PAYMENT UPON DELIVERY (BEFORE UNLOADING)',
  'CREDIT',
  'OTHER',
] as const

/** The four a converted case carries AS THEMSELVES (QW-11, S-12).
 *
 * Conversion maps each of these onto a payment-terms kind of its own and
 * materialises its instalments (`caseTermsKind`, `backend/cmd/asa/conversion.go`).
 * CREDIT is carried since S-12 with the quote's day count — or, on a legacy
 * credit quote stored without one, with the count the conversion sheet asks
 * for. OTHER lands on the case as `other`, and so does any text the picker did
 * not write: one instalment dated at registration. The conversion confirmation warns about
 * exactly those, so this set is a DISPLAY mirror of that switch, not a rule of
 * the client's own; `paymentOptions.test.ts` reads the Go switch and fails when
 * the two part. */
export const carriedPaymentOptionValues = [
  'IN ADVANCE PAYMENT',
  '50/50 PAYMENT (UPON PICKUP&DELIVERY)',
  'FULL PAYMENT UPON DELIVERY (BEFORE UNLOADING)',
  'CREDIT',
] as const satisfies readonly (typeof paymentOptionValues)[number][]

/** The i18n key for one value, e.g. `quotes.paymentOptions.credit`. */
function optionKey(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `quotes.paymentOptions.${slug}`
}

type Translate = (key: string, fallback?: { defaultValue: string }) => string

/** One stored value as the picker words it: the translated label for one of
 *  the five, and the stored text itself for anything else — wording that
 *  predates the list reads back exactly as it was typed. */
export function paymentOptionLabel(value: string, t: Translate): string {
  return paymentOptionValues.some((option) => option === value)
    ? t(optionKey(value), { defaultValue: value })
    : value
}

/** The picker's options, with any stored value that is not on the list kept as
 *  its own entry so an older quote's terms survive an edit untouched. */
export function paymentOptions({
  current,
  t,
}: {
  current: string
  t: Translate
}): { value: string; label: string }[] {
  // Annotated rather than inferred: `paymentOptionValues` is `as const`, so an
  // inferred element type would be the five literals and the unshift below —
  // whose value is whatever the quote already stores — would not fit in it.
  const options: { value: string; label: string }[] = paymentOptionValues.map(
    (value) => ({ value, label: paymentOptionLabel(value, t) }),
  )

  const stored = current.trim()
  if (stored !== '' && !paymentOptionValues.some((value) => value === stored)) {
    // First, not appended: it is what this quote actually says, and burying it
    // under five it does not say would read as though the field were unset.
    options.unshift({ value: stored, label: stored })
  }

  return options
}
